// @ts-check
import uniq from "lodash/uniq";
import last from "lodash/last";
import flatMapDeep from "lodash/flatMapDeep";
import flattenDeep from "lodash/flattenDeep";
import groupBy from "lodash/groupBy";
import lineDefaults from "~/components/visualization/lineDefaults";
import ChartColors from "~/components/visualization/chartColors";

/**
 * [DatasetFactory description]
 *  @namespace
 */
const DatasetFactory = {
  /**
   *
   * @param {object[]} harvestData Array of results returned from the api call.
   * @param {string|object[]} fieldName Field in query.sel.
   * @param {object} query The selector object used to obtain harvestData.
   * @returns {object[]} Array of Chartjs datasets.
   */
  create(harvestData, fieldName, query) {
    const years = uniq(harvestData.map((h) => h.year));
    let fieldValue, data, datasets;
    if (Array.isArray(fieldName)) {
      // Do something here
      data = this.dependantDataEntries(years, harvestData, fieldName);
      datasets = this.createDatasets(data, last(fieldName).fieldName);
    } else if (fieldName) {
      fieldValue = query?.sel ? query?.sel[fieldName]?.$in : undefined;
      data = this.dataEntries(fieldValue, years, harvestData, fieldName);
      datasets = this.createDatasets(data, fieldName);
    } else {
      data = this.dataEntries(null, years, harvestData, null);
      datasets = this.createDatasets(data, null);
    }
    return datasets;
  },

  /**
   *
   * @param {Array.<{fieldName: string, values: string[]}>} fieldNames Array of
   * objects specifying the field and selected values.
   * @param {number[]} years Array of years to collect data for.
   * @param {object[]} harvestData Array of results returned from the api call.
   * @returns {Array.<{groupData: object[], groupDescription: string}>}
   */
  createGroups(fieldNames, years, harvestData) {
    // Example fieldNames:
    //   [
    //     { fieldName: "county", values: ["CHAMPAIGN", "PIATT"] },
    //     { fieldName: "gender", values: ["M", "F"] },
    //   ],

    let groupedData = [];
    const groupings = fieldNames.map((obj, idx) => {
      const fieldName = obj.fieldName;
      const values = obj.values;
      if (idx === 0) {
        groupedData = values.map((v) => {
          const modifiedValue = v.toLowerCase().replace(/\s|\./g, "");
          const groupDescription = `${fieldName}-${v}`;
          const groupData = flatMapDeep(years, (yr) => {
            let val = harvestData.filter(
              (h) =>
                h.year === yr &&
                h[fieldName].toLowerCase().replace(/\s|\./g, "") ===
                  modifiedValue
            );
            return val;
          });
          return {
            groupData,
            groupDescription,
          };
        });
      } else {
        groupedData = flattenDeep(
          groupedData.map((gd) => {
            const data = gd.groupData;
            const tmpData = values.map((v) => {
              const groupDescription = `${gd.groupDescription}-${fieldName}-${v}`;
              const groupData = years.map((yr) =>
                data.filter((h) => h.year === yr && h[fieldName] === v)
              );
              return {
                groupData,
                groupDescription,
              };
            });
            tmpData.forEach(
              (obj2) => (obj2.groupData = flattenDeep(obj2.groupData))
            );
            return tmpData;
          })
        );
      }
      return groupedData;
    });
    const flatGroupings = flattenDeep(groupings);
    let count = 0;
    flatGroupings.forEach((obj) => {
      count = obj.groupDescription.match(/-/g).length;
    });
    if (count === 1) {
      return flatGroupings;
    } else {
      const filteredGroupings = flatGroupings.filter(
        (g) => g.groupDescription.match(/-/g).length === count
      );
      return filteredGroupings;
    }
  },

  /**
   *
   * @param {number[]} years The years to plot.
   * @param {object[]} harvestData Array of results returned from the api call.
   * @param {object[]} fieldNames The query object selector field.
   * @returns object[][] An array of arrays of values.
   */
  dependantDataEntries(years, harvestData, fieldNames) {
    let groupings,
      data = [[]];
    const fieldNamesClone = [...fieldNames];
    if (fieldNames.length) {
      const sumField = fieldNamesClone.pop();
      groupings = this.createGroups(fieldNames, years, harvestData);
      // const fieldName = sumField.fieldName;
      // const values = sumField.values;
      data = groupings.map((g) => {
        return years.map((yr) => {
          let val = g.groupData
            .filter((h) => h.year === yr)
            .reduce((acc, obj) => (acc = acc + obj.value), 0);
          if (val === 0) {
            val = null;
          }
          return {
            year: yr,
            selector: `${g.groupDescription}`,
            value: val,
          };
        });
      });
      const flattenedData = groupBy(flattenDeep(data), (obj) => obj.selector);
      data = Object.entries(flattenedData).map((a) => a[1]);
    }
    console.log({ groupings });
    console.log({ data });
    return data;
  },

  /**
   *
   * @param {string[]} field The query selector field values.
   * @param {number[]} years The years to plot.
   * @param {object[]} harvestData Array of results returned from the api call.
   * @param {string} fieldName The query object selector field.
   * @returns object[][] An array of arrays of object values.
   */
  dataEntries(field, years, harvestData, fieldName) {
    let data;
    if (field?.length) {
      data = field.map((f) => {
        const selectorLabel = f;
        return years.map((yr) => {
          let val = harvestData
            .filter((h) => {
              if (fieldName) {
                const modifiedFieldName = h[fieldName]
                  .toLowerCase()
                  .replace(/\s/, "");
                return h.year === yr && modifiedFieldName === f.toLowerCase();
              } else {
                return h.year === yr;
              }
            })
            .reduce((acc, currentVal) => {
              acc = acc + currentVal.value;
              return acc;
            }, 0);
          if (val === 0) {
            val = null;
          }
          return {
            year: yr,
            selector: `${fieldName}-${selectorLabel}`,
            value: val,
          };
        });
      });
    } else {
      // This is the default value
      data = [
        years.map((yr) => {
          let val = harvestData
            .filter((h) => h.year === yr)
            .reduce((acc, currentVal) => {
              acc = acc + currentVal.value;
              return acc;
            }, 0);
          if (val === 0) {
            val = null;
          }
          return {
            year: yr,
            selector: `-`,
            value: val,
          };
        }),
      ];
    }
    return data;
  },

  /**
   *
   * @param {object[][]} data An array of arrays of values.
   * @param {string} fieldName The query object selector field.
   * @returns {object[]} The Chartjs datasets to plot.
   */
  createDatasets(data, fieldName) {
    let datasets = [];
    if (data?.length) {
      data.forEach((d) => {
        const dataPoints = uniq(d.map((obj) => obj.value));
        if (dataPoints.length !== 0 && dataPoints[0] !== null) {
          let selectorString = "Statewide ";
          // let selectorString = "";
          if (fieldName) {
            selectorString = this.selectorString(fieldName, d);
            // console.log({ selectorString });
            if (selectorString !== "-") {
              selectorString += " ";
            } else {
              selectorString = "";
            }
          }
          const dataset = Object.assign(
            {},
            lineDefaults,
            {
              label: `${selectorString}Deer Harvested`,
              yAxisID: "y-axis-0",
              type: "line",
              backgroundColor: ChartColors.black,
              // borderColor: ChartColors.totalBorder,
              pointHoverBorderColor: ChartColors.black,
              data: d.map((obj) => obj.value),
            },
            this.lineColors(selectorString)
          );
          datasets.push(dataset);
        }
      });
    }
    return datasets;
  },

  selectorString(_fieldName, value) {
    return value[0].selector;
    // let str = value[0].selector.slice(value[0].selector.indexOf("-") + 1);
    // switch (true) {
    //   case fieldName === "gender" && str === "M":
    //     str = "Male";
    //     break;
    //   case fieldName === "gender" && str === "F":
    //     str = "Female";
    //     break;
    //   case fieldName === "ageClass" && str === "A":
    //     str = "Adult";
    //     break;
    //   case fieldName === "ageClass" && str === "Y":
    //     str = "Yearling";
    //     break;
    //   case fieldName === "ageClass" && str === "F":
    //     str = "Fawn";
    //     break;
    //   default:
    //     break;
    // }
    // return str;
  },

  lineColors(selector) {
    let options = {};
    if (selector === "Male") {
      options = {
        backgroundColor: ChartColors.adultBuckBorder,
        borderColor: ChartColors.adultBuckBorder,
        pointHoverBorderColor: ChartColors.adultBuckBorder,
      };
    }
    if (selector === "Female") {
      options = {
        backgroundColor: ChartColors.adultDoeBorder,
        borderColor: ChartColors.adultDoeBorder,
        pointHoverBorderColor: ChartColors.adultDoeBorder,
      };
    }
    if (selector === "Adult") {
      options = {
        backgroundColor: ChartColors.adultBuckBorder,
        borderColor: ChartColors.adultBuckBorder,
        pointHoverBorderColor: ChartColors.adultBuckBorder,
      };
    }
    if (selector === "Yearling") {
      options = {
        backgroundColor: ChartColors.yearlingBuckBorder,
        borderColor: ChartColors.yearlingBuckBorder,
        pointHoverBorderColor: ChartColors.yearlingBuckBorder,
      };
    }
    if (selector === "Fawn") {
      options = {
        backgroundColor: ChartColors.doeFawnBorder,
        borderColor: ChartColors.doeFawnBorder,
        pointHoverBorderColor: ChartColors.doeFawnBorder,
      };
    }
    if (selector === "Male Adult") {
      options = {
        backgroundColor: ChartColors.adultBuckBorder,
        borderColor: ChartColors.adultBuckBorder,
        pointHoverBorderColor: ChartColors.adultBuckBorder,
      };
    }
    if (selector === "Male Yearling") {
      options = {
        backgroundColor: ChartColors.yearlingBuckBorder,
        borderColor: ChartColors.yearlingBuckBorder,
        pointHoverBorderColor: ChartColors.yearlingBuckBorder,
      };
    }
    if (selector === "Male Fawn") {
      options = {
        backgroundColor: ChartColors.antlerlessBorder,
        borderColor: ChartColors.antlerlessBorder,
        pointHoverBorderColor: ChartColors.antlerlessBorder,
      };
    }
    if (selector === "Female Adult") {
      options = {
        backgroundColor: ChartColors.adultDoeBorder,
        borderColor: ChartColors.adultDoeBorder,
        pointHoverBorderColor: ChartColors.adultDoeBorder,
      };
    }
    // if (selector === "Female Yearling") {
    //   options = {
    //     backgroundColor: ChartColors.yearlingBuckBorder,
    //     borderColor: ChartColors.yearlingBuckBorder,
    //     pointHoverBorderColor: ChartColors.yearlingBuckBorder
    //   };
    // }
    if (selector === "Female Fawn") {
      options = {
        backgroundColor: ChartColors.doeFawnBorder,
        borderColor: ChartColors.doeFawnBorder,
        pointHoverBorderColor: ChartColors.doeFawnBorder,
      };
    }

    return options;
  },
};

export default DatasetFactory;
